import { useState, useEffect } from "react";

// react-router-dom components
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

import { useAuth } from "context/AuthContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { loginUser } from "services/apiService";
import AlertBox from "components/Alert/alert";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState(""); //admin@affordacarghana.com
  const [password, setPassword] = useState(""); //Admin@123

  const [alert, setAlert] = useState({
    open: false,
    type: "success", // "success" | "error" | "confirm"
    title: "",
    message: "",
  });

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberEmail");
    const savedPassword = localStorage.getItem("rememberPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (rememberMe) {
      localStorage.setItem("rememberEmail", email);
      localStorage.setItem("rememberPassword", password);
    } else {
      localStorage.removeItem("rememberEmail");
      localStorage.removeItem("rememberPassword");
    }
  }, [rememberMe, email, password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (email === "") {
      setAlert({
        open: true,
        type: "error",
        title: "Ooops!",
        message: "Please enter valid email.",
      });
    } else if (password === "") {
      setAlert({
        open: true,
        type: "error",
        title: "Ooops!",
        message: "Please enter the passoword.",
      });
    } else {
      try {
        const res = await loginUser(email, password);
        if (res?.success !== true) {
          setAlert({
            open: true,
            type: "error",
            title: "Ooops!",
            message: res?.message || "Login failed",
          });
        } else {
          login(res?.data, res?.data?.token);

          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("Login error:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Ooops!",
          message: err?.message || "Login failed",
        });
      }
    }
  };

  return (
    <BasicLayout>
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => {
          setAlert({ ...alert, open: false });
        }}
      />
      <Card
        sx={{
          boxShadow: "0 2px 12px rgba(66, 161, 70, 0.12)",
          border: ({ palette }) => `1px solid ${palette.info.main}22`,
        }}
      >
        <MDBox pt={5} pb={1} px={4}>
          <MDTypography variant="h4" fontWeight="bold">
            Welcome back
          </MDTypography>
          <MDTypography variant="body2" color="text" mt={0.5}>
            Sign in to your admin account to continue
          </MDTypography>
        </MDBox>
        <MDBox pt={3} pb={4} px={4}>
          <MDBox component="form" role="form" onSubmit={handleLogin}>
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="medium" color="text" mb={0.5}>
                Email
              </MDTypography>
              <MDInput
                type="email"
                placeholder="you@example.com"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon fontSize="small" color="info" />
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="medium" color="text" mb={0.5}>
                Password
              </MDTypography>
              <MDInput
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" color="info" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>
            <MDBox mt={3} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                sx={{ textTransform: "none", fontWeight: 600, py: 1.2 }}
              >
                Sign in
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
